/**
 * Admin Settings Modal Component - Manage YouTube cookies and system settings
 */
import React, { useState, useEffect } from 'react';
import { adminAPI, cookiesAPI } from '../services/api';
import './AdminSettingsModal.css';

const AdminSettingsModal = ({ onClose }) => {
  const [settings, setSettings] = useState(null);
  const [cookiesInput, setCookiesInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getSettings();
      setSettings(response.data);
      setTestResult(response.data.cookies?.test_result);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(err.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCookies = async () => {
    if (!cookiesInput.trim()) {
      setError('Please enter cookies content');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await adminAPI.updateCookies(cookiesInput);

      setSuccess('Cookies saved successfully!');
      setTestResult(response.data.test_result);

      // Reload settings after saving
      await loadSettings();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error saving cookies:', err);
      setError(err.response?.data?.error || 'Failed to save cookies');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCookies = async () => {
    if (!window.confirm('Are you sure you want to remove YouTube cookies? This may cause download failures due to bot detection.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await adminAPI.deleteCookies();

      setSuccess('Cookies removed successfully');
      setTestResult(null);
      setCookiesInput('');

      // Reload settings after removing
      await loadSettings();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error removing cookies:', err);
      setError(err.response?.data?.error || 'Failed to remove cookies');
    } finally {
      setSaving(false);
    }
  };

  const handleTestDownload = async () => {
    try {
      setTesting(true);
      setError(null);
      setTestResult(null);

      const response = await cookiesAPI.testCookies();
      setTestResult(response.data.test_result);
      setSuccess('✅ Cookies are working! YouTube access verified.');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error testing download:', err);
      const errorMsg = err.response?.data?.test_result?.message || err.response?.data?.error || 'Test download failed';
      setError(errorMsg);
      setTestResult({ success: false, message: errorMsg });
    } finally {
      setTesting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      setError('Please upload a .txt file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await cookiesAPI.uploadFile(file);
      setTestResult(response.data.test_result);

      setSuccess('✅ Cookies uploaded and tested successfully!');
      setCookiesInput(''); // Clear text input

      // Reload settings after upload
      await loadSettings();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error uploading cookies:', err);
      setError(err.response?.data?.error || 'Failed to upload cookies');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const getCookiesStatus = () => {
    if (!settings?.cookies) return 'unknown';

    if (settings.cookies.available) {
      if (testResult?.success) return 'working';
      return 'available';
    }
    return 'missing';
  };

  const getStatusMessage = () => {
    const status = getCookiesStatus();

    switch (status) {
      case 'working':
        return '✓ Cookies are working correctly';
      case 'available':
        return '⚠ Cookies are saved but may need testing';
      case 'missing':
        return '✗ No cookies configured';
      default:
        return 'Status unknown';
    }
  };

  const getStatusClass = () => {
    const status = getCookiesStatus();
    return `status-badge ${status}`;
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Admin Settings</h2>
            <button className="btn-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="loading">Loading settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content admin-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Admin Settings</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              {error}
              <button className="alert-close" onClick={() => setError(null)}>&times;</button>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
              <button className="alert-close" onClick={() => setSuccess(null)}>&times;</button>
            </div>
          )}

          {/* YouTube Cookies Section */}
          <div className="settings-section">
            <h3>YouTube Authentication</h3>
            <p className="section-description">
              Configure YouTube cookies to bypass bot detection and improve download success rates.
            </p>

            {/* Cookie Status */}
            <div className="cookie-status">
              <div className="status-header">
                <span className="status-label">Current Status:</span>
                <span className={getStatusClass()}>{getStatusMessage()}</span>
              </div>

              {settings?.ytdlp_available !== undefined && (
                <div className="status-item">
                  <span className="label">yt-dlp:</span>
                  <span className={`value ${settings.ytdlp_available ? 'available' : 'unavailable'}`}>
                    {settings.ytdlp_available ? '✓ Available' : '✗ Not Installed'}
                  </span>
                </div>
              )}
            </div>

            {/* Test Download Button */}
            <div className="test-section">
              <button
                className="btn-test"
                onClick={handleTestDownload}
                disabled={testing}
              >
                {testing ? 'Testing...' : '🧪 Test Download'}
              </button>

              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'failed'}`}>
                  <strong>Test Result:</strong> {testResult.message}
                </div>
              )}
            </div>

            {/* Cookie Input - Enhanced with File Upload */}
            <div className="cookie-input-section">
              <div className="input-methods">
                {/* Method 1: File Upload */}
                <div className="upload-method">
                  <h4>📁 Upload cookies.txt</h4>
                  <input
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    disabled={uploading || saving}
                    id="cookie-file-input"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="cookie-file-input" className="upload-btn">
                    {uploading ? '🔄 Uploading...' : '📁 Choose File'}
                  </label>
                </div>

                <div className="method-divider">
                  <span>or</span>
                </div>

                {/* Method 2: Text Paste */}
                <div className="paste-method">
                  <h4>📋 Paste cookie content</h4>
                  <textarea
                    id="cookies-input"
                    value={cookiesInput}
                    onChange={(e) => setCookiesInput(e.target.value)}
                    placeholder="# Netscape HTTP Cookie File
.youtube.com	TRUE	/	FALSE	1735689664	SID	.your-session-id-here
.youtube.com	TRUE	/	FALSE	1735689664	HSID	.your-hsid-here
..."
                    rows={8}
                    className="cookies-textarea"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="cookie-help">
                <details>
                  <summary>📖 How to get YouTube cookies</summary>
                  <div className="help-content">
                    <ol>
                      <li>Install the "Get cookies.txt LOCALLY" browser extension</li>
                      <li>Go to YouTube and make sure you're logged in</li>
                      <li>Click the extension button and export cookies</li>
                      <li>Upload the file using the button above, OR copy the content and paste it</li>
                      <li>System will automatically test if cookies work</li>
                    </ol>
                    <p className="security-note">
                      <strong>🔒 Security Note:</strong> Cookies are stored securely with proper file permissions (0o600).
                      Never share your cookies file publicly.
                    </p>
                  </div>
                </details>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="cookie-actions">
              <button
                className="btn-save-cookies"
                onClick={handleSaveCookies}
                disabled={saving || !cookiesInput.trim()}
              >
                {saving ? '💾 Saving...' : '💾 Save Pasted Cookies'}
              </button>

              <button
                className="btn-test-cookies"
                onClick={handleTestDownload}
                disabled={testing || !settings?.cookies?.available}
              >
                {testing ? '🧪 Testing...' : '🧪 Test Cookies'}
              </button>

              {settings?.cookies?.available && (
                <button
                  className="btn-remove-cookies"
                  onClick={handleRemoveCookies}
                  disabled={saving}
                >
                  🗑️ Remove Cookies
                </button>
              )}
            </div>
          </div>

          {/* Bot Detection Information */}
          <div className="info-section">
            <h4>ℹ️ About Bot Detection</h4>
            <p>
              YouTube has implemented aggressive bot detection that prevents automated downloads without proper authentication.
              When bot detection occurs, downloads will fail immediately with "sign in to confirm" errors.
            </p>
            <p>
              <strong>Solution:</strong> Adding valid YouTube cookies allows the downloader to authenticate as a regular user,
              dramatically improving success rates and preventing IP bans.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsModal;
